import { UserMediaAssetDTO } from '../dto/user-media-asset.dto';
import { PlatformScheduleDTO } from '../dto/platform-schedule.dto';
import { PlatformScheduleGroupDTO } from '../dto/platform-schedule-group.dto';
import { gql } from '@apollo/client';
import { plainToInstance, classToPlain } from 'class-transformer';
import { CaptionToggleRemoveType } from '../interfaces/caption-type';

const UPDATE_USER_MEDIA_ASSET_MUTATION = gql`
  mutation ($input: UserMediaAssetInput!) {
    userMediaAsset(input: $input) {
      id
      caption
      contentType
      isCustomize
      userMediaAssetsMetadata {
        id
        name
        value
      }
    }
  }
`;

const PLATFORM_SCHEDULES_QUERY = gql`
  query ($filter: PlatformSchedulesFilterInput!, $groupFilter: PlatformScheduleGroupsFilterInput!, $isCustomCaptions: Boolean) {
    platformSchedules(filter: $filter, isCustomCaptions: $isCustomCaptions) {
      data {
        id
        caption
        scheduleDate
        posted
        error
        isEnabled
        status
        collaborators
        __typename
        platformScheduleAssets {
          id
          sortOrder
          userMediaAsset {
            tag
            cdnId
            urls
            caption
            isCustomize
            id
            userMediaAssetsMetadata {
              id
              name
              value
            }
          }
        }
      }
      next
    }
    platformScheduleGroups(filter: $groupFilter) {
      data {
        id
        groupDate
        isDraft
        hasOtherPlatforms
        platformSchedules {
          id
          caption
          scheduleDate
          posted
          error
          isEnabled
          status
          platformSchedulePostType
          collaborators
          __typename
          platformScheduleAssets {
            id
            sortOrder
            contentType
            urls
            metadata
            userMediaAsset {
              tag
              cdnId
              urls
              caption
              isCustomize
              id
              userMediaAssetsMetadata {
                id
                name
                value
              }
            }
          }
          platformScheduleComment {
            comment
            createdAt
            error
            id
            isPosted
            socialPlatformId
            updatedAt
          }
          platform {
            name
          }
        }
      }
      next
    }
  }
`;

const UPLOAD_LIMIT_QUERY = gql`
  query {
    getLimitUpload {
      limitUpload
      uploadsRemaining
      uploadsDone
    }
  }
`;

const IG_FEED_QUERY = gql`
  query ($workspaceId: Int!, $type: String) {
    getIGFeed(workspaceId: $workspaceId, type: $type) {
      id
      groupDate
      isDraft
      isExternal
      hasOtherPlatforms
      platformSchedules {
        id
        caption
        scheduleDate
        posted
        error
        isEnabled
        status
        platformSchedulePostType
        collaborators
        __typename
        platformScheduleAssets {
          id
          sortOrder
          contentType
          urls
        }
        platform {
          id
          name
        }
      }
    }
  }
`;

export enum TabTypeKeys {
  UPDATE_USER_MEDIA_ASSET_START = 'UPDATE_USER_MEDIA_ASSET_START',
  UPDATE_USER_MEDIA_ASSET_SUCCESS = 'UPDATE_USER_MEDIA_ASSET_SUCCESS',
  UPDATE_USER_MEDIA_ASSET_FAILURE = 'UPDATE_USER_MEDIA_ASSET_FAILURE',
  ADD_USER_MEDIA_ASSETS = 'ADD_USER_MEDIA_ASSETS',
  ADD_USER_MEDIA_ASSETS_SUCCESS = 'ADD_USER_MEDIA_ASSETS_SUCCESS',
  UPLOAD_USER_MEDIA_ASSETS_SUCCESS = 'UPLOAD_USER_MEDIA_ASSETS_SUCCESS',
  UPDATE_PLATFORM_SCHEDULE_START = 'UPDATE_PLATFORM_SCHEDULE_START',
  UPDATE_PLATFORM_SCHEDULE_SUCCESS = 'UPDATE_PLATFORM_SCHEDULE_SUCCESS',
  UPDATE_PLATFORM_SCHEDULE_FAILURE = 'UPDATE_PLATFORM_SCHEDULE_FAILURE',
  UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_START = 'UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_START',
  UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_SUCCESS = 'UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_SUCCESS',
  UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_FAILURE = 'UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_FAILURE',
  UNSCHEDULE_PLATFORM_SCHEDULE_SUCCESS = 'UNSCHEDULE_PLATFORM_SCHEDULE_SUCCESS',
  UPLOAD_LIMIT_START = 'UPLOAD_LIMIT_START',
  UPLOAD_LIMIT_SUCCESS = 'UPLOAD_LIMIT_SUCCESS',
  FETCH_SCHEDULE_START = 'FETCH_SCHEDULE_START',
  FETCH_SCHEDULE_SUCCESS = 'FETCH_SCHEDULE_SUCCESS',
  FETCH_IG_FEED_SUCCESS = 'FETCH_IG_FEED_SUCCESS'
}

export enum VideoStates {
  ALL = 'all',
  SCHEDULED = 'scheduled',
  UNSCHEDULED = 'draft',
  POSTED = 'completed',
  ERRORED = 'ERRORED'
}

export enum PlatformScheduleGroupFilters {
  ALL = 'ALL',
  SCHEDULED = 'SCHEDULED',
  DRAFT = 'DRAFT',
  POSTED = 'POSTED',
  ERRORED = 'ERRORED'
}

export enum Sources {
  COMPUTER = 'computer',
  DROPBOX = 'dropbox',
  DRIVE = 'drive'
}

export interface UpdateUserMediaAssetStartAction {
  type: TabTypeKeys.UPDATE_USER_MEDIA_ASSET_START;
  userMediaAsset: UserMediaAssetDTO;
}

export interface UpdateUserMediaAssetSuccessAction {
  type: TabTypeKeys.UPDATE_USER_MEDIA_ASSET_SUCCESS;
  userMediaAsset: UserMediaAssetDTO;
}

export interface UpdateUserMediaAssetFailureAction {
  type: TabTypeKeys.UPDATE_USER_MEDIA_ASSET_FAILURE;
  error: string;
}

export interface AddUserAssetsAction {
  type: TabTypeKeys.ADD_USER_MEDIA_ASSETS;
  userMediaAsset: UserMediaAssetDTO;
}

export interface AddMediaAssetsSuccessAction {
  type: TabTypeKeys.ADD_USER_MEDIA_ASSETS_SUCCESS;
}

export interface UploadMediaAssetsSuccessAction {
  type: TabTypeKeys.UPLOAD_USER_MEDIA_ASSETS_SUCCESS;
  userMediaAsset: UserMediaAssetDTO;
  file: File;
}

export interface UpdatePlatformScheduleStartAction {
  type: TabTypeKeys.UPDATE_PLATFORM_SCHEDULE_START;
  platformSchedule: PlatformScheduleDTO;
}

export interface UpdatePlatformScheduleSuccessAction {
  type: TabTypeKeys.UPDATE_PLATFORM_SCHEDULE_SUCCESS;
  platformSchedule: PlatformScheduleDTO;
}

export interface UpdatePlatformScheduleFailureAction {
  type: TabTypeKeys.UPDATE_PLATFORM_SCHEDULE_FAILURE;
  error: string;
}

export interface UpdateMediaToPlatformScheduleStartAction {
  type: TabTypeKeys.UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_START;
}

export interface UpdateMediaToPlatformScheduleSuccessAction {
  type: TabTypeKeys.UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_SUCCESS;
}

export interface UpdateMediaToPlatformScheduleFailureAction {
  type: TabTypeKeys.UPDATE_MEDIA_TO_PLATFORM_SCHEDULE_FAILURE;
  error: string;
}

export interface UnschedulePlatformScheduleSuccessAction {
  type: TabTypeKeys.UNSCHEDULE_PLATFORM_SCHEDULE_SUCCESS;
  id: number;
}

export interface UploadLimitStartAction {
  type: TabTypeKeys.UPLOAD_LIMIT_START;
  loading: boolean;
}

export interface UploadLimitSuccessAction {
  type: TabTypeKeys.UPLOAD_LIMIT_SUCCESS;
  limitUpload: number;
  uploadsRemaining: number;
  uploadsDone: number;
  loading: boolean;
}

export interface FetchScheduledStartAction {
  type: TabTypeKeys.FETCH_SCHEDULE_START;
}

export interface FetchScheduledSuccessAction {
  type: TabTypeKeys.FETCH_SCHEDULE_SUCCESS;
  scheduledPostsWithoutGroup: PlatformScheduleDTO[];
  scheduledGroups: PlatformScheduleGroupDTO[];
}

export interface FetchIgFeedSuccessAction {
  type: TabTypeKeys.FETCH_IG_FEED_SUCCESS;
  igFeedPosts: PlatformScheduleGroupDTO[];
}

export type TabActions =
  | UpdateUserMediaAssetStartAction
  | UpdateUserMediaAssetSuccessAction
  | UpdateUserMediaAssetFailureAction
  | AddUserAssetsAction
  | AddMediaAssetsSuccessAction
  | UploadMediaAssetsSuccessAction
  | UpdatePlatformScheduleStartAction
  | UpdatePlatformScheduleSuccessAction
  | UpdatePlatformScheduleFailureAction
  | UpdateMediaToPlatformScheduleStartAction
  | UpdateMediaToPlatformScheduleSuccessAction
  | UpdateMediaToPlatformScheduleFailureAction
  | UnschedulePlatformScheduleSuccessAction
  | UploadLimitStartAction
  | UploadLimitSuccessAction
  | FetchScheduledStartAction
  | FetchScheduledSuccessAction
  | FetchIgFeedSuccessAction;

/**
 * Helper to get the current platform account ID FOR THE OLD POSTING EXPEREINCE
 */
export const getPlaformAccountId = (currentAccount, metaDataObj) => {
  const platformAccounts = currentAccount?.platformAccounts;
  let platformAccount = null;

  metaDataObj.forEach((item) => {
    if (item.name === 'tiktokToggle' && item.value === 'true') {
      platformAccount = platformAccounts.find((account) => account.type === 'tiktok');
    }
  });

  // Use the 'planoly' account
  if (!platformAccount) {
    platformAccount = platformAccounts.find((account) => account.type === 'planoly');
  }

  // Finally throw an error if we can't find an account
  if (platformAccount === null || platformAccount === undefined) {
    throw 'Account NOT found';
  }

  return platformAccount.id;
};

/**
 * Update User Media Asset
 *
 * TODO:
 * This method requires a refactor to handle multiple metadata.
 * It should not make assumptions about what metadata is always received
 */
export const updateUserMediaAsset = (userMediaAsset: UserMediaAssetDTO) => async (dispatch, getState) => {
  dispatch({
    type: TabTypeKeys.UPDATE_USER_MEDIA_ASSET_START
  });

  const variables: any = {
    input: {
      id: userMediaAsset.id,
      caption: userMediaAsset.caption,
      cdnId: userMediaAsset.cdnId,
      contentType: userMediaAsset.contentType,
      tag: userMediaAsset.tag,
      isCustomize: userMediaAsset.isCustomize
    },
    isCustomCaptions: true
  };

  let metadataObj;

  if (userMediaAsset && userMediaAsset.userMediaAssetsMetadata !== undefined && userMediaAsset.userMediaAssetsMetadata.length > 0) {
    userMediaAsset.userMediaAssetsMetadata = userMediaAsset.userMediaAssetsMetadata.filter(
      (meta) => ![`${CaptionToggleRemoveType.FACEBOOK}`, `${CaptionToggleRemoveType.LINKEDIN}`, `${CaptionToggleRemoveType.TWITTER}`].includes(meta.name)
    );
    metadataObj = classToPlain(userMediaAsset.userMediaAssetsMetadata);
    variables.input.userMediaAssetsMetadata = metadataObj;
  }

  try {
    const response = await getState().graphClient.mutate({
      mutation: UPDATE_USER_MEDIA_ASSET_MUTATION,
      variables: variables
    });

    dispatch({
      type: TabTypeKeys.UPDATE_USER_MEDIA_ASSET_SUCCESS,
      userMediaAsset: plainToInstance(UserMediaAssetDTO, response.data.userMediaAsset)
    });

    if (variables.input.userMediaAssetsMetadata) {
      document.dispatchEvent(
        new CustomEvent('segmentEvent', {
          detail: {
            eventName: 'TikTok_Sound_URL_Used',
            properties: {
              soundUrl: variables.input.userMediaAssetsMetadata.name
            }
          }
        })
      );
    }
  } catch (error) {
    dispatch({
      type: TabTypeKeys.UPDATE_USER_MEDIA_ASSET_FAILURE,
      error: error.message
    });
  }
};

export const uploadMediaAssetSuccess = (userMediaAsset: UserMediaAssetDTO, file: File) => async (dispatch) => {
  dispatch({
    type: TabTypeKeys.UPLOAD_USER_MEDIA_ASSETS_SUCCESS,
    userMediaAsset: userMediaAsset,
    file: file
  });
};

/**
 * Fetch All Platform Schedules
 */
export const fetchScheduled = () => async (dispatch, getState) => {
  dispatch({
    type: TabTypeKeys.FETCH_SCHEDULE_START
  });

  try {
    const response = await getState().graphClient.query({
      query: PLATFORM_SCHEDULES_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          isPosted: false,
          excludeInGroup: true,
          page: 1,
          limit: 100
        },
        groupFilter: {
          type: PlatformScheduleGroupFilters.ALL,
          page: 1,
          limit: 100
        },
        isCustomCaptions: true
      }
    });

    // Individual platform_schedules without a group
    const platformSchedules: PlatformScheduleDTO[] = response.data.platformSchedules.data
      ? plainToInstance<PlatformScheduleDTO, Object>(PlatformScheduleDTO, response.data.platformSchedules.data)
      : [];

    for (const schedule of platformSchedules) {
      for (const asset of schedule.platformScheduleAssets) {
        asset.userMediaAsset = plainToInstance<UserMediaAssetDTO, Object>(UserMediaAssetDTO, asset.userMediaAsset);
      }
    }

    // Platform Schedule Groups and child platform_schedules
    const platformScheduleGroups: PlatformScheduleGroupDTO[] = response.data.platformScheduleGroups?.data
      ? plainToInstance<PlatformScheduleGroupDTO, Object>(PlatformScheduleGroupDTO, response.data.platformScheduleGroups.data)
      : [];

    for (const group of platformScheduleGroups) {
      for (const groupSchedule of group.platformSchedules) {
        for (const groupAsset of groupSchedule.platformScheduleAssets) {
          groupAsset.userMediaAsset = plainToInstance<UserMediaAssetDTO, Object>(UserMediaAssetDTO, groupAsset.userMediaAsset);
        }
      }
    }

    dispatch({
      type: TabTypeKeys.FETCH_SCHEDULE_SUCCESS,
      scheduledPostsWithoutGroup: platformSchedules,
      scheduledGroups: platformScheduleGroups
    });
  } catch (error) {
    console.log(error);
  }
};

/**
 * getAccountLimit
 */
export const getAccountLimit = () => async (dispatch, getState) => {
  dispatch({
    type: TabTypeKeys.UPLOAD_LIMIT_START,
    loading: true
  });

  try {
    const response = await getState().graphClient.query({
      query: UPLOAD_LIMIT_QUERY,
      fetchPolicy: 'no-cache'
    });

    dispatch({
      type: TabTypeKeys.UPLOAD_LIMIT_SUCCESS,
      limitUpload: response.data.getLimitUpload.limitUpload,
      uploadsRemaining: response.data.getLimitUpload.uploadsRemaining,
      uploadsDone: response.data.getLimitUpload.uploadsDone,
      loading: false
    });
  } catch (e) {
    console.log(e);
  }
};

export const getIGFeed = (workspaceId: number, type?: string) => async (dispatch, getState) => {
  if (type === 'story') {
    dispatch({
      type: TabTypeKeys.FETCH_IG_FEED_SUCCESS,
      igFeedPosts: []
    });
    return;
  }
  const postType = type === 'grid-post' ? 'GRID' : 'REEL';

  try {
    const response = await getState().graphClient.query({
      query: IG_FEED_QUERY,
      variables: {
        workspaceId: workspaceId,
        type: postType
      },
      fetchPolicy: 'no-cache'
    });
    dispatch({
      type: TabTypeKeys.FETCH_IG_FEED_SUCCESS,
      igFeedPosts: plainToInstance<PlatformScheduleGroupDTO, Object>(PlatformScheduleGroupDTO, response.data.getIGFeed)
    });
  } catch (e) {
    console.log(e);
  }
};
